/**
 * The root shell — the port of `excellent/app/layout.tsx` (604 LOC) and
 * `app/_chrome/client-layout.tsx` (RO-0-05, PRD R6: "the root layout is a
 * keystone", single-owner, ported once).
 *
 * Everything L3 mounts lands inside this file's `{children}`, so the contract it
 * owes downstream is exact: the same provider stack in the same ORDER, the same
 * DOM, the same class names, the same scroll container. This is a TRANSPORT
 * change (PRD §4.1) — a page that looks different after the port is a defect, so
 * nothing here is an improvement on the original, deliberately. Where a class
 * name looks odd, it is load-bearing and the original carries the reason.
 *
 * ── What is here, and what is a hole ──
 *
 * PORTED, working:
 *   · the provider stack (order preserved exactly — see below)
 *   · the takeover branch: `/welcome`, `/login`, `/transparency`,
 *     `/integrations/connected`, `/onboarding/workspace` render bare
 *   · the full chrome DOM: the three-column row, the `--gut` bleed, the scroll
 *     container with its `--content-inset` / 1200px reading cap
 *   · the route-fade transition, keyed on path + `area`/`view`/`sub` but NOT the
 *     pulse drill params
 *   · the one-shot `?dock=terminal` deep link — openTerminal() + strip
 *     (RO-11-04 / #44589; DockProvider lives in this file)
 *   · the redirect cascade (in `redirects.ts`, thrown from the root route)
 *   · DockBar + BottomNav fill keyboardNav / overlays (RO-11-04)
 *
 * NOT PORTED — remaining chrome COMPONENTS. `TopNav` and `RailChrome` are
 * async SERVER components that read SQLite; `ModeRail`, `LeftRail`,
 * `ContextRail`, `TerminalPane`, and the rest either land in later RO-11 tasks
 * or still import `next/navigation` directly. They live in `excellent/app/`,
 * which this item may not modify and RO-10-09 deletes — so importing them would
 * build the new stack on top of the tree it replaces.
 *
 * They are therefore SLOTS (`ShellChrome`), each named for the component that
 * belongs in it. A slot renders nothing until its component is ported. That is
 * the difference between a declared hole and a silent omission: the shape is
 * here, typed and documented, so L3/L4 fill it in rather than rediscover it.
 */

import * as React from "react";
import { SWRConfig } from "swr";
import { ShellSWRProvider } from "./swr-provider";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { SidebarProvider } from "@excellent/ui/sidebar-context";
import { RailExtensionProvider } from "@excellent/ui/rail-extension-context";
import { ModeSubNavProvider } from "@excellent/ui/mode-subnav-context";
import { VocabProvider } from "@excellent/ui/vocab-context";
import { CrumbLabelProvider } from "@excellent/ui/crumb-label-context";
import type { WorkspaceLexicon } from "@excellent/core/vocab";
import { isTakeoverPath } from "./redirects";
import type { ShellData } from "./shell-data";
import { DockProvider, useDock } from "./chrome/dock-context";
import { StageProvider } from "./chrome/stage-context";
import { AppPlayLaunchProvider } from "./chrome/app-play-launch-provider";

/**
 * The chrome regions the layout renders around a page. Every one is optional and
 * defaults to nothing; the name is the component that must land there.
 *
 * Ordered as they appear in `app/layout.tsx` so a reader can diff the two files
 * top to bottom.
 */
export interface ShellChrome {
  /** `DesktopTitleBar` — null in a browser; stamps `<html data-desktop-chrome>`. */
  titleBar?: React.ReactNode;
  /** `TopNav` (async server component: scope, reviewCount, enabledSections). */
  topNav?: React.ReactNode;
  /** `ModeRail` — the six modes, absolutely positioned in the left margin. */
  modeRail?: React.ReactNode;
  /** `LeftRail topSlot={<RailChrome/>}` — gated; Database + the docs viewer only. */
  leftRail?: React.ReactNode;
  /** `ContextRail` — page-contextual nav in the reserved right margin. */
  contextRail?: React.ReactNode;
  /** `TerminalPane` — bottom-docked inside the main column. */
  terminal?: React.ReactNode;
  /** `BottomNav`, `TaskPeekHost`, `ObjectPeekHost`, `StageHost`, `ScrollActivity`. */
  overlays?: React.ReactNode;
  /** `KeyboardNavProvider` — a leaf sibling, NOT a wrapper. Keep it that way. */
  keyboardNav?: React.ReactNode;
}

export interface ShellProps {
  data: ShellData;
  chrome?: ShellChrome;
  /** SWR config the shell installs globally (`chrome.swrConfigs` in the original). */
  swrConfig?: React.ComponentProps<typeof SWRConfig>["value"];
  children: React.ReactNode;
}

/**
 * The main column: scroll container on top, terminal pane docked below.
 *
 * Three things about the scroll container are contracts, not styling, and each
 * has already been broken once:
 *   1. `children` is a DIRECT child of the definite-height scroll element, so
 *      `PageNav`'s `sticky top-0` travels the full scrollHeight and `h-full`
 *      panes resolve. A centering wrapper div breaks both.
 *   2. The side padding is `max(rail clearance, 1200px cap)` ON the scroll
 *      element — not a narrower column — so the wheel works over the full row
 *      instead of leaving dead zones under the floating rails.
 *   3. The route fade lives on this element and is opacity-only; a transform
 *      would change the sticky header's containing block.
 */
function MainColumn({
  children,
  terminal,
  onScroll,
}: {
  children: React.ReactNode;
  terminal?: React.ReactNode;
  onScroll: (top: number) => void;
}) {
  const scrollRef = React.useRef<HTMLDivElement>(null);

  // Page identity — what a transition fires on. Path plus the IA query params,
  // but NOT the pulse drill params (g/t/c), so drilling a bar doesn't fade the
  // whole page.
  const navKey = useLocation({
    select: (l) => {
      const sp = new URLSearchParams(l.searchStr);
      return `${l.pathname}|${sp.get("area") ?? ""}|${sp.get("view") ?? ""}|${sp.get("sub") ?? ""}`;
    },
  });
  const prevKey = React.useRef<string | null>(null);

  // A LAYOUT effect, before paint: otherwise the new page paints at full opacity
  // for a frame and then drops, which is itself the flash this is avoiding.
  React.useLayoutEffect(() => {
    if (prevKey.current === null) {
      prevKey.current = navKey; // skip the cold paint
      return;
    }
    if (prevKey.current === navKey) return;
    prevKey.current = navKey;
    const el = scrollRef.current;
    if (!el) return;
    el.classList.remove("route-fade");
    void el.offsetWidth; // reflow, so remove + re-add restarts the animation
    el.classList.add("route-fade");
  }, [navKey]);

  return (
    <main className="flex-1 min-w-0 flex flex-col">
      <div
        ref={scrollRef}
        // `pb-*` reserves clearance so content scrolls clear of the floating
        // DockBar, which sits higher on mobile than on desktop.
        className="@container/page flex-1 min-h-0 overflow-auto pb-36 md:pb-24"
        style={{
          paddingInline:
            "max(var(--content-inset, 0px), calc((100% - calc(var(--spacing) * 300)) / 2))",
        }}
        onScroll={(e) => onScroll(e.currentTarget.scrollTop)}
      >
        {children}
      </div>
      {terminal}
    </main>
  );
}

/**
 * Consume the one-shot `?dock=terminal` deep link (the menu-bar "Open in
 * Terminal" item): OPEN the terminal dock via DockProvider, then STRIP the
 * param so the URL reflects real state. A manual refresh won't force a closed
 * dock back open, and re-firing the menu item re-triggers because the value
 * goes ⌀→terminal again. A left-in param makes the second click a no-op.
 *
 * RO-11-04 / #44589: openTerminal() is wired — was strip-only before DockProvider
 * landed in this shell.
 */
function useDockDeepLink(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const { openTerminal } = useDock();
  const dockParam = new URLSearchParams(location.searchStr).get("dock");

  React.useEffect(() => {
    if (dockParam !== "terminal") return;
    openTerminal();
    const params = new URLSearchParams(location.searchStr);
    params.delete("dock");
    const qs = params.toString();
    void navigate({ to: `${location.pathname}${qs ? `?${qs}` : ""}`, replace: true });
  }, [dockParam, navigate, location.pathname, location.searchStr, openTerminal]);
}

/**
 * Inner body — always rendered under DockProvider so useDockDeepLink can call
 * openTerminal(). Takeover paths still get the provider (unused) rather than
 * forking the hook.
 */
function ShellBody({
  data: _data,
  chrome = {},
  swrConfig,
  children,
}: ShellProps) {
  const pathname = useLocation({ select: (l) => l.pathname });
  const takeover = isTakeoverPath(pathname);

  useDockDeepLink();

  // The shell's real scroll container is the page area, not the window, so it is
  // the single source of scroll direction for the floating nav's
  // hide-on-scroll-down behaviour. `ChromeScrollProvider` is not ported; the
  // callback is the seam it plugs into.
  const reportScroll = React.useCallback((_top: number) => {}, []);

  if (takeover) return <>{children}</>;

  // ShellSWRProvider is the Linear-style client cache (persist + keepPrevious
  // + MutateBridge). Optional `swrConfig` merges on top for tests. StageProvider
  // sits inside so Stage polls share the same cache as the rest of the shell.
  const body = (
    <StageProvider>
      <AppPlayLaunchProvider>
      <SidebarProvider>
        <CrumbLabelProvider>
          <RailExtensionProvider>
            <ModeSubNavProvider>
              <div className="relative h-[calc(100vh-var(--desktop-titlebar-h,0px))] flex flex-col bg-background">
                {chrome.topNav}
                {/*
                  Three visually-independent columns: navigation rail · the
                  application · context rail. The GAP, not a shared border,
                  is what reads each as its own surface.

                  `md:px-36` reserves the ModeRail's width (w-36) as
                  SYMMETRIC left+right padding. The rail is absolutely
                  positioned against this row's padding box, so the left
                  reserve pushes the in-flow columns clear of it WITHOUT
                  moving the rail; the matching right reserve is what
                  re-centres the 1200px content well on the full viewport
                  instead of biasing it right by the nav's width.
                */}
                <div className="page-scroll-row relative flex-1 min-h-0 flex gap-3 px-3 md:px-36 pb-3 pt-[var(--nav-clearance)]">
                  {chrome.modeRail}
                  {chrome.leftRail}
                  {/*
                    The column BLEEDS back out under the floating rails via a
                    negative `--gut` margin, so the scroll element spans the
                    full width and the wheel scrolls from anywhere; `--gut` is
                    re-added as the scroll element's padding, so content lands
                    pixel-identically. Capping the width HERE instead would
                    leave non-scrolling dead zones on either side.
                  */}
                  <div
                    className="flex flex-1 min-w-0 overflow-hidden"
                    style={{ marginInline: "calc(-1 * var(--gut))" }}
                  >
                    <MainColumn terminal={chrome.terminal} onScroll={reportScroll}>
                      {children}
                    </MainColumn>
                  </div>
                  {chrome.contextRail}
                </div>
              </div>
              {/*
                Leaf siblings INSIDE ModeSubNavProvider and OUTSIDE the
                full-height div — BottomNav, the two peek hosts, the apps
                stage, the scroll reporter. Their placement is why a peek
                can overlay the whole shell; do not nest them into the
                column.
              */}
              {chrome.overlays}
            </ModeSubNavProvider>
          </RailExtensionProvider>
          {/* KeyboardNavProvider / DockBar is a leaf, not a wrapper. It sits
              inside CrumbLabelProvider but outside RailExtensionProvider. */}
          {chrome.keyboardNav}
        </CrumbLabelProvider>
      </SidebarProvider>
    </AppPlayLaunchProvider>
    </StageProvider>
  );

  if (swrConfig) {
    return <SWRConfig value={swrConfig}>{body}</SWRConfig>;
  }
  return <ShellSWRProvider>{body}</ShellSWRProvider>;
}

export function Shell({ data, chrome = {}, swrConfig, children }: ShellProps) {
  // `ThemeProvider` is not ported — the theme classes are applied to <html> in
  // `main.tsx` (see `theme.ts`), which is what the Next tree did server-side.
  // DockProvider wraps the whole shell so useDockDeepLink + TerminalPane share
  // one terminal dock state (RO-11-04 / #44589).
  // StageProvider (inside ShellBody, under SWRConfig) owns Stage open/tabs
  // so Play and Chat share one tab set.
  return (
    <VocabProvider lexicon={(data.lexicon as WorkspaceLexicon | null) ?? null}>
      {chrome.titleBar}
      <DockProvider>
        <ShellBody data={data} chrome={chrome} swrConfig={swrConfig}>
          {children}
        </ShellBody>
      </DockProvider>
    </VocabProvider>
  );
}
